// SCR-STAFF-887 (AP-ACC-02) — زبانه‌های پروفایل نرم‌افزار مقصد (AC-ACC-040.2)، زمان‌بندی خروجی (AC-ACC-041.1) و تطبیق با مقصد (AC-ACC-043.1).
// پروفایل‌های آماده «نمونه» (IsSample) فقط برای کپی‌اند؛ نمونه خروجی پایلوت هنوز نیست (C-AGENT-1446).
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  unwrap,
  type AccExportColumn,
  type AccExportDateFormat,
  type AccExportDelimiter,
  type AccExportEncoding,
  type AccExportField,
  type AccExportFormat,
  type AccExportFrequency,
  type AccExportProfileSpec,
  type AccExportProfileView,
  type AccExportScheduleView,
  type AccReconciliationRow,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  applyServerErrors,
  SelectRHF,
  TextFieldRHF,
  toBusinessDate,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { accKeys, Amount, fileToBase64, useAccBranches, useBranchName } from './common';

export const EXPORT_FIELDS: readonly AccExportField[] = [
  'VoucherNumber',
  'VoucherDate',
  'VoucherDescription',
  'LineNo',
  'AccountCode',
  'Detail1',
  'Detail2',
  'Detail3',
  'Debit',
  'Credit',
  'SignedAmount',
  'LineDescription',
];
const ENCODINGS: readonly AccExportEncoding[] = ['Utf8Bom', 'Utf8', 'Utf16Le', 'Windows1256'];
const DELIMITERS: readonly AccExportDelimiter[] = ['Comma', 'Semicolon', 'Tab'];
const DATE_FORMATS: readonly AccExportDateFormat[] = [
  'JalaliSlash',
  'JalaliDash',
  'JalaliCompact',
  'GregorianIso',
];
const FORMATS: readonly AccExportFormat[] = ['Csv', 'Xlsx'];
const FREQUENCIES: readonly AccExportFrequency[] = ['Daily', 'Weekly', 'Monthly'];

/** اعتبار ستون‌ها مانند سرور (کد حساب و یکی از «بدهکار و بستانکار» یا «مبلغ علامت‌دار»؛ منطق خالص — تست Vitest). */
export function columnsValid(columns: readonly AccExportColumn[]): boolean {
  const fields = columns.map((column) => column.field);
  const has = (field: AccExportField) => fields.includes(field);
  const split = has('Debit') && has('Credit');
  return (
    columns.length > 0 &&
    columns.length <= 20 &&
    new Set(fields).size === fields.length &&
    columns.every((column) => column.header.trim().length > 0 && column.header.trim().length <= 60) &&
    has('AccountCode') &&
    split !== has('SignedAmount') &&
    has('Debit') === has('Credit')
  );
}

// ── پروفایل‌ها ─────────────────────────────────────────────────────────────────────────────────

const profileSchema = z.object({
  code: z.string().regex(/^[A-Za-z0-9_-]{1,40}$/, { error: 'acc:profile.code_invalid' }),
  name: z.string().trim().min(1, { error: 'common:validation.required' }).max(100),
  software: z.string().trim().min(1, { error: 'common:validation.required' }).max(100),
});
type ProfileForm = z.input<typeof profileSchema>;

export function ProfilesTab({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const profiles = useQuery({
    queryKey: accKeys.profiles(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/export-profiles')),
  });
  const [editing, setEditing] = useState<{
    profile: AccExportProfileView | null;
    presetCode: string | null;
    spec: AccExportProfileSpec;
  } | null>(null);
  const columns: DataTableColumn<AccExportProfileView>[] = [
    { id: 'name', headerKey: 'acc:profile.name', cell: (row) => row.name },
    { id: 'code', headerKey: 'acc:common.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'software', headerKey: 'acc:profile.software', cell: (row) => row.software },
    {
      id: 'format',
      headerKey: 'acc:export.format',
      cell: (row) => t(`acc:export.format_${row.spec.format}`),
    },
    {
      id: 'encoding',
      headerKey: 'acc:profile.encoding',
      cell: (row) => t(`acc:encoding.${row.spec.encoding}`),
    },
    {
      id: 'date',
      headerKey: 'acc:profile.date_format',
      cell: (row) => t(`acc:date_format.${row.spec.dateFormat}`),
    },
    {
      id: 'status',
      headerKey: 'acc:common.status',
      cell: (row) => t(row.isActive ? 'acc:common.active' : 'acc:common.inactive'),
    },
    {
      id: 'actions',
      headerKey: 'acc:common.actions',
      cell: (row) =>
        canManage ? (
          <Button
            size="small"
            onClick={() => setEditing({ profile: row, presetCode: null, spec: row.spec })}
            data-testid="acc-profile-edit"
          >
            {t('acc:common.edit')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Stack spacing={2}>
      {canManage ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap aria-label={t('acc:profile.presets')}>
          {(profiles.data?.presets ?? []).map((preset) => (
            <Button
              key={preset.code}
              variant="outlined"
              size="small"
              onClick={() => setEditing({ profile: null, presetCode: preset.code, spec: preset.spec })}
              data-testid="acc-profile-preset"
              data-code={preset.code}
            >
              {t('acc:profile.copy_preset', { name: preset.name })}
              {preset.isSample ? <Chip size="small" label={t('acc:profile.sample')} sx={{ ms: 1 }} /> : null}
            </Button>
          ))}
        </Stack>
      ) : null}
      <Alert severity="info">{t('acc:profile.pilot_hint')}</Alert>
      <DataTable
        captionKey="acc:profile.title"
        columns={columns}
        rows={profiles.data?.items}
        getRowId={(row) => row.id}
        isLoading={profiles.isPending}
        error={profiles.error}
        onRetry={() => void profiles.refetch()}
        emptyTitleKey="acc:profile.empty"
      />
      {editing ? <ProfileDialog initial={editing} onClose={() => setEditing(null)} /> : null}
    </Stack>
  );
}

function ProfileDialog({
  initial,
  onClose,
}: {
  initial: { profile: AccExportProfileView | null; presetCode: string | null; spec: AccExportProfileSpec };
  onClose: () => void;
}) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [spec, setSpec] = useState<AccExportProfileSpec>(initial.spec);
  const [active, setActive] = useState(initial.profile?.isActive ?? true);
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      code: initial.profile?.code ?? '',
      name: initial.profile?.name ?? '',
      software: initial.profile?.software ?? '',
    },
  });
  const valid = columnsValid(spec.columns);
  const save = useMutation({
    mutationFn: (form: ProfileForm) => {
      const body = {
        code: form.code.trim(),
        name: form.name.trim(),
        software: form.software.trim(),
        presetCode: initial.presetCode,
        spec,
        isActive: active,
      };
      return initial.profile
        ? unwrap(
            tenantApi.PUT('/api/v1/accounting/export-profiles/{id}', {
              params: { path: { id: initial.profile.id } },
              body,
            }),
          )
        : unwrap(
            tenantApi.POST('/api/v1/accounting/export-profiles', {
              body,
              headers: { 'Idempotency-Key': key.current() },
            }),
          );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accKeys.profiles(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name', 'software'],
        codeFields: { 'PHX-ACC-0504': 'code' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  const updateColumn = (index: number, change: Partial<AccExportColumn>) =>
    setSpec({
      ...spec,
      columns: spec.columns.map((column, position) =>
        position === index ? { ...column, ...change } : column,
      ),
    });
  const move = (index: number, delta: number) => {
    const next = [...spec.columns];
    const [item] = next.splice(index, 1);
    if (item) {
      next.splice(index + delta, 0, item);
      setSpec({ ...spec, columns: next });
    }
  };
  const unused = EXPORT_FIELDS.filter((field) => !spec.columns.some((column) => column.field === field));
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-887-PROFILE"
      titleKey={initial.profile ? 'acc:profile.edit' : 'acc:profile.add'}
      maxWidth="md"
      onSubmit={() => (valid ? void handleSubmit((form) => save.mutate(form))() : undefined)}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextFieldRHF
            control={control}
            name="code"
            labelKey="acc:common.code"
            required
            fullWidth
            disabled={Boolean(initial.profile)}
            slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'acc-profile-code' } }}
          />
          <TextFieldRHF
            control={control}
            name="name"
            labelKey="acc:profile.name"
            required
            fullWidth
            slotProps={{ htmlInput: { 'data-testid': 'acc-profile-name' } }}
          />
          <TextFieldRHF
            control={control}
            name="software"
            labelKey="acc:profile.software"
            required
            fullWidth
            slotProps={{ htmlInput: { 'data-testid': 'acc-profile-software' } }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            fullWidth
            label={t('acc:export.format')}
            value={spec.format}
            onChange={(event) => setSpec({ ...spec, format: event.target.value as AccExportFormat })}
          >
            {FORMATS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`acc:export.format_${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label={t('acc:profile.encoding')}
            value={spec.encoding}
            onChange={(event) => setSpec({ ...spec, encoding: event.target.value as AccExportEncoding })}
            disabled={spec.format === 'Xlsx'}
          >
            {ENCODINGS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`acc:encoding.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label={t('acc:profile.delimiter')}
            value={spec.delimiter}
            onChange={(event) => setSpec({ ...spec, delimiter: event.target.value as AccExportDelimiter })}
            disabled={spec.format === 'Xlsx'}
          >
            {DELIMITERS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`acc:delimiter.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label={t('acc:profile.date_format')}
            value={spec.dateFormat}
            onChange={(event) => setSpec({ ...spec, dateFormat: event.target.value as AccExportDateFormat })}
          >
            {DATE_FORMATS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`acc:date_format.${value}`)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <FormControlLabel
          control={
            <Checkbox
              checked={spec.includeHeader}
              onChange={(event) => setSpec({ ...spec, includeHeader: event.target.checked })}
            />
          }
          label={t('acc:profile.include_header')}
        />
        <Typography variant="subtitle2">{t('acc:profile.columns')}</Typography>
        {spec.columns.map((column, index) => (
          <Stack
            key={column.field}
            direction="row"
            spacing={1}
            alignItems="center"
            data-testid="acc-profile-column"
            data-field={column.field}
          >
            <Typography sx={{ minWidth: 160 }}>{t(`acc:field.${column.field}`)}</Typography>
            <TextField
              size="small"
              label={t('acc:profile.header')}
              value={column.header}
              onChange={(event) => updateColumn(index, { header: event.target.value })}
            />
            <IconButton
              aria-label={t('acc:profile.move_up')}
              disabled={index === 0}
              onClick={() => move(index, -1)}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label={t('acc:profile.move_down')}
              disabled={index === spec.columns.length - 1}
              onClick={() => move(index, 1)}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label={t('acc:profile.remove_column')}
              onClick={() =>
                setSpec({ ...spec, columns: spec.columns.filter((_, position) => position !== index) })
              }
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        {unused.length > 0 ? (
          <TextField
            select
            size="small"
            label={t('acc:profile.add_column')}
            value=""
            onChange={(event) =>
              setSpec({
                ...spec,
                columns: [
                  ...spec.columns,
                  {
                    field: event.target.value as AccExportField,
                    header: t(`acc:field.${event.target.value}`),
                  },
                ],
              })
            }
            sx={{ maxWidth: 260 }}
          >
            {unused.map((field) => (
              <MenuItem key={field} value={field}>
                {t(`acc:field.${field}`)}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        {!valid ? (
          <Alert severity="warning" data-testid="acc-profile-invalid">
            {t('acc:profile.columns_invalid')}
          </Alert>
        ) : null}
        {initial.profile ? (
          <FormControlLabel
            control={<Checkbox checked={active} onChange={(event) => setActive(event.target.checked)} />}
            label={t('acc:common.active')}
          />
        ) : null}
      </Stack>
    </FormDialog>
  );
}

// ── زمان‌بندی ──────────────────────────────────────────────────────────────────────────────────

const scheduleSchema = z.object({
  name: z.string().trim().min(1, { error: 'common:validation.required' }).max(100),
  frequency: z.string(),
  profileId: z.string(),
  branchId: z.string(),
});
type ScheduleForm = z.input<typeof scheduleSchema>;

export function SchedulesTab({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const branchName = useBranchName();
  const schedules = useQuery({
    queryKey: accKeys.schedules(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/export-schedules')),
  });
  const [editing, setEditing] = useState<AccExportScheduleView | 'new' | null>(null);
  const run = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/accounting/export-schedules/:run')),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: accKeys.all(session?.tenantId) }),
  });
  const columns: DataTableColumn<AccExportScheduleView>[] = [
    { id: 'name', headerKey: 'acc:schedule.name', cell: (row) => row.name },
    {
      id: 'frequency',
      headerKey: 'acc:schedule.frequency',
      cell: (row) => t(`acc:frequency.${row.frequency}`),
    },
    {
      id: 'profile',
      headerKey: 'acc:schedule.profile',
      cell: (row) => row.profileName ?? t('acc:schedule.generic'),
    },
    {
      id: 'branch',
      headerKey: 'acc:common.branch',
      cell: (row) => branchName(row.branchId) ?? t('acc:common.all'),
    },
    {
      id: 'next',
      headerKey: 'acc:schedule.next_period',
      cell: (row) => (
        <span data-testid="acc-schedule-next">
          <JalaliDate value={row.nextPeriodStart} /> – <JalaliDate value={row.nextPeriodEnd} />
        </span>
      ),
    },
    {
      id: 'last',
      headerKey: 'acc:schedule.last_period',
      cell: (row) => (row.lastPeriodEnd ? <JalaliDate value={row.lastPeriodEnd} /> : '—'),
    },
    {
      id: 'status',
      headerKey: 'acc:common.status',
      cell: (row) => t(row.isActive ? 'acc:common.active' : 'acc:common.inactive'),
    },
    {
      id: 'actions',
      headerKey: 'acc:common.actions',
      cell: (row) =>
        canManage ? (
          <Button size="small" onClick={() => setEditing(row)}>
            {t('acc:common.edit')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Stack spacing={2}>
      <Alert severity="info">{t('acc:schedule.hint')}</Alert>
      {canManage ? (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setEditing('new')} data-testid="acc-schedule-add">
            {t('acc:schedule.add')}
          </Button>
          <Button onClick={() => run.mutate()} disabled={run.isPending} data-testid="acc-schedule-run">
            {t('acc:schedule.run')}
          </Button>
        </Stack>
      ) : null}
      {run.data ? (
        <Alert severity="success" data-testid="acc-schedule-result">
          {t('acc:schedule.result', { batches: run.data.batches, waiting: run.data.waiting })}
        </Alert>
      ) : null}
      {run.error ? <ErrorState error={run.error} compact /> : null}
      <DataTable
        captionKey="acc:schedule.title"
        columns={columns}
        rows={schedules.data?.items}
        getRowId={(row) => row.id}
        isLoading={schedules.isPending}
        error={schedules.error}
        onRetry={() => void schedules.refetch()}
        emptyTitleKey="acc:schedule.empty"
      />
      {editing ? (
        <ScheduleDialog schedule={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
    </Stack>
  );
}

function ScheduleDialog({
  schedule,
  onClose,
}: {
  schedule: AccExportScheduleView | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const branches = useAccBranches();
  const [active, setActive] = useState(schedule?.isActive ?? true);
  const [submitError, setSubmitError] = useState<unknown>();
  const profiles = useQuery({
    queryKey: accKeys.profiles(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/export-profiles')),
  });
  const { control, handleSubmit, formState, setError } = useForm<ScheduleForm>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: {
      name: schedule?.name ?? '',
      frequency: schedule?.frequency ?? 'Monthly',
      profileId: schedule?.profileId ?? '',
      branchId: schedule?.branchId ?? '',
    },
  });
  const save = useMutation({
    mutationFn: (form: ScheduleForm) => {
      const body = {
        name: form.name.trim(),
        frequency: form.frequency as AccExportFrequency,
        profileId: form.profileId || null,
        branchId: form.branchId || null,
        isActive: active,
      };
      return schedule
        ? unwrap(
            tenantApi.PUT('/api/v1/accounting/export-schedules/{id}', {
              params: { path: { id: schedule.id } },
              body,
            }),
          )
        : unwrap(
            tenantApi.POST('/api/v1/accounting/export-schedules', {
              body,
              headers: { 'Idempotency-Key': key.current() },
            }),
          );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accKeys.schedules(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['name', 'frequency', 'profileId', 'branchId'],
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-887-SCHEDULE"
      titleKey={schedule ? 'acc:schedule.edit' : 'acc:schedule.add'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <TextFieldRHF
          control={control}
          name="name"
          labelKey="acc:schedule.name"
          required
          fullWidth
          slotProps={{ htmlInput: { 'data-testid': 'acc-schedule-name' } }}
        />
        <SelectRHF
          control={control}
          name="frequency"
          labelKey="acc:schedule.frequency"
          fullWidth
          options={FREQUENCIES.map((value) => ({ value, labelKey: `acc:frequency.${value}` }))}
        />
        <SelectRHF
          control={control}
          name="profileId"
          labelKey="acc:schedule.profile"
          fullWidth
          options={[
            { value: '', labelKey: 'acc:schedule.generic' },
            ...(profiles.data?.items ?? [])
              .filter((profile) => profile.isActive)
              .map((profile) => ({ value: profile.id, label: profile.name ?? undefined })),
          ]}
        />
        <SelectRHF
          control={control}
          name="branchId"
          labelKey="acc:common.branch"
          fullWidth
          options={[
            { value: '', labelKey: 'acc:common.all' },
            ...branches.map((branch) => ({ value: branch.branchId, label: branch.name ?? undefined })),
          ]}
        />
        {schedule ? (
          <FormControlLabel
            control={<Checkbox checked={active} onChange={(event) => setActive(event.target.checked)} />}
            label={t('acc:common.active')}
          />
        ) : null}
      </Stack>
    </FormDialog>
  );
}

// ── تطبیق با مقصد ──────────────────────────────────────────────────────────────────────────────

interface RangeForm {
  from: string | null;
  to: string | null;
}

export function ReconciliationTab() {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const today = toBusinessDate(new Date());
  const { control, watch } = useForm<RangeForm>({ defaultValues: { from: today, to: today } });
  const from = watch('from') ?? today;
  const to = watch('to') ?? today;
  const [profileId, setProfileId] = useState('');
  const profiles = useQuery({
    queryKey: accKeys.profiles(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/export-profiles')),
  });
  const report = useQuery({
    queryKey: accKeys.reconciliation(session?.tenantId, `${from}|${to}`),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/reports/export-reconciliation', {
          params: { query: { from, to } },
        }),
      ),
  });
  const compare = useMutation({
    mutationFn: async (file: File) =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/reports/export-reconciliation:compare', {
          body: {
            fromDate: from,
            toDate: to,
            branchId: null,
            profileId: profileId || null,
            fileName: file.name,
            contentBase64: await fileToBase64(file),
          },
        }),
      ),
  });
  const view = compare.data ?? report.data;
  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      compare.mutate(file);
    }
  };
  const columns: DataTableColumn<AccReconciliationRow>[] = [
    {
      id: 'account',
      headerKey: 'acc:journal.account',
      cell: (row) => <bdi dir="ltr">{row.accountCode}</bdi>,
    },
    { id: 'title', headerKey: 'acc:common.title', cell: (row) => row.accountTitle ?? '—' },
    {
      id: 'phoenix_debit',
      headerKey: 'acc:reconcile.phoenix_debit',
      cell: (row) => <Amount money={row.phoenixDebit} />,
    },
    {
      id: 'phoenix_credit',
      headerKey: 'acc:reconcile.phoenix_credit',
      cell: (row) => <Amount money={row.phoenixCredit} />,
    },
    {
      id: 'exported_debit',
      headerKey: 'acc:reconcile.exported_debit',
      cell: (row) => <Amount money={row.exportedDebit} />,
    },
    {
      id: 'exported_credit',
      headerKey: 'acc:reconcile.exported_credit',
      cell: (row) => <Amount money={row.exportedCredit} />,
    },
    {
      id: 'open_debit',
      headerKey: 'acc:reconcile.open_debit',
      cell: (row) => <Amount money={row.notExportedDebit} />,
    },
    {
      id: 'open_credit',
      headerKey: 'acc:reconcile.open_credit',
      cell: (row) => <Amount money={row.notExportedCredit} />,
    },
    ...(view?.compared
      ? [
          {
            id: 'dest_debit',
            headerKey: 'acc:reconcile.destination_debit',
            cell: (row: AccReconciliationRow) => <Amount money={row.destinationDebit} />,
          },
          {
            id: 'dest_credit',
            headerKey: 'acc:reconcile.destination_credit',
            cell: (row: AccReconciliationRow) => <Amount money={row.destinationCredit} />,
          },
          {
            id: 'difference',
            headerKey: 'acc:reconcile.difference',
            cell: (row: AccReconciliationRow) => <Amount money={row.difference} />,
          },
        ]
      : []),
  ];
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <JalaliDatePickerRHF control={control} name="from" labelKey="acc:common.from" />
        <JalaliDatePickerRHF control={control} name="to" labelKey="acc:common.to" />
        <TextField
          select
          size="small"
          label={t('acc:reconcile.profile')}
          value={profileId}
          onChange={(event) => setProfileId(event.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">{t('acc:schedule.generic')}</MenuItem>
          {(profiles.data?.items ?? []).map((profile) => (
            <MenuItem key={profile.id} value={profile.id}>
              {profile.name}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" component="label" data-testid="acc-reconcile-upload">
          {t('acc:reconcile.upload')}
          <input
            hidden
            type="file"
            accept=".csv,.txt,.xlsx"
            onChange={onFile}
            data-testid="acc-reconcile-file"
          />
        </Button>
        {compare.data ? (
          <Button onClick={() => compare.reset()} data-testid="acc-reconcile-clear">
            {t('acc:reconcile.clear')}
          </Button>
        ) : null}
      </Stack>
      {compare.error ? <ErrorState error={compare.error} compact /> : null}
      {view ? (
        <Alert
          severity={view.mismatches === 0 ? 'success' : 'warning'}
          data-testid="acc-reconcile-summary"
          data-mismatches={view.mismatches}
        >
          {view.mismatches === 0
            ? t(view.compared ? 'acc:reconcile.zero_compared' : 'acc:reconcile.zero')
            : t('acc:reconcile.mismatches', { count: view.mismatches })}
        </Alert>
      ) : null}
      <DataTable
        captionKey="acc:reconcile.title"
        columns={columns}
        rows={view?.rows}
        getRowId={(row) => row.accountCode}
        isLoading={report.isPending}
        error={report.error}
        onRetry={() => void report.refetch()}
        emptyTitleKey="acc:reconcile.empty"
      />
    </Stack>
  );
}
